/**
 * The one read the three non-table views share (`specs/M4-V2-kanban.md` §5,
 * `specs/M4-V4-calendar.md` §5, `specs/M4-V7-gallery.md` §5).
 *
 * None of them adds a server endpoint: they all ask `BoardDataSource.load()` for the same
 * filtered, sorted board the table gets, optionally with B4 §8.8's `groupBy` — which is what
 * turns a kanban's lanes into group-by buckets — and page it with the same cursors.
 */
import type { BoardId, GroupId, ViewId } from '@elizmonday/shared';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  BoardDataSource,
  BoardViewPayload,
  ItemRow,
  QueryState,
} from '../../board-view/data/types';
import type { CellRenderContext } from '../../board-view/columns/types';

export interface ViewDataState {
  readonly payload: BoardViewPayload | null;
  readonly pending: boolean;
  readonly error: Error | null;
  /** Rows appended by `loadMore`, per group id. */
  readonly extra: Readonly<Record<string, readonly ItemRow[]>>;
  readonly cursors: Readonly<Record<string, string | null>>;
  /**
   * The query state these cursors belong to.
   *
   * A cursor is only valid for the query that produced it — the sources hash the filter,
   * sort and search into it and reject a mismatch with `invalid_cursor`. The state can change
   * *during* a render (the calendar narrows its read to the visible days as soon as the
   * board's columns arrive, `shared/date-range.ts`), and `loadAll` is called from that same
   * render, so without this the next page is asked for with the previous query's cursor.
   */
  readonly key: string;
}

export interface UseViewDataResult extends ViewDataState {
  /** Every row of one group, first page plus whatever `loadMore` has appended. */
  rowsOf(groupId: string): readonly ItemRow[];
  /** Every row of every group, in group order. */
  allRows(): readonly ItemRow[];
  loadMore(groupId: string): void;
  /** Page every group until it is exhausted or `ceiling` rows have been read. */
  loadAll(ceiling: number): void;
  refresh(): void;
  context: CellRenderContext;
}

export function useViewData(input: {
  source: BoardDataSource | null;
  boardId: string;
  viewId: string;
  state?: Partial<QueryState>;
}): UseViewDataResult {
  const { source, boardId, viewId } = input;
  // The query state is an object literal at every call site; hashing it keeps the effect from
  // re-running on every render, which would make the board flicker forever.
  const stateKey = JSON.stringify(input.state ?? {});
  const [data, setData] = useState<ViewDataState>({
    payload: null,
    pending: true,
    error: null,
    extra: {},
    cursors: {},
    key: stateKey,
  });
  const [nonce, setNonce] = useState(0);
  const loading = useRef(new Set<string>());

  useEffect(() => {
    if (source === null) return;
    let cancelled = false;
    /*
     * A **re-read** of the same query keeps painting what it has: `pending` is "there is nothing
     * to show yet", and the kinds guard on it (`if (probe.pending) return <PageSkeleton/>`), so
     * flipping it on every refresh threw the whole view away and rebuilt it — which since P-140
     * would happen on somebody else's cell write, with the open settings pane going with it.
     */
    setData((previous) => ({
      ...previous,
      pending: previous.payload === null || previous.key !== stateKey,
      error: null,
    }));
    const state = JSON.parse(stateKey) as Partial<QueryState>;
    source
      .load({ boardId: boardId as BoardId, viewId: viewId as ViewId, state })
      .then((payload) => {
        if (cancelled) return;
        const cursors: Record<string, string | null> = {};
        for (const group of payload.groups) cursors[group.id] = group.page.cursor;
        setData({ payload, pending: false, error: null, extra: {}, cursors, key: stateKey });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setData((previous) => ({
          ...previous,
          pending: false,
          error: error instanceof Error ? error : new Error(String(error)),
        }));
      });
    return () => {
      cancelled = true;
    };
  }, [source, boardId, viewId, stateKey, nonce]);

  /** Realtime: the board room's own change stream re-reads, exactly as the grid does. */
  useEffect(() => {
    if (source === null) return undefined;
    return source.subscribe(() => setNonce((value) => value + 1));
  }, [source]);

  const loadMore = useCallback(
    (groupId: string) => {
      if (source === null) return;
      // Cursors from a previous query are not ours to page with (see `key`).
      if (data.key !== stateKey) return;
      const cursor = data.cursors[groupId];
      if (cursor === null || cursor === undefined || loading.current.has(groupId)) return;
      loading.current.add(groupId);
      const state = JSON.parse(stateKey) as Partial<QueryState>;
      void source
        .page({
          boardId: boardId as BoardId,
          viewId: viewId as ViewId,
          groupId: groupId as GroupId,
          cursor,
          state,
        })
        .then((page) => {
          setData((previous) => ({
            ...previous,
            extra: { ...previous.extra, [groupId]: [...(previous.extra[groupId] ?? []), ...page.items] },
            cursors: { ...previous.cursors, [groupId]: page.cursor },
          }));
        })
        .finally(() => loading.current.delete(groupId));
    },
    [source, boardId, viewId, stateKey, data.cursors, data.key],
  );

  const rowsOf = useCallback(
    (groupId: string): readonly ItemRow[] => {
      const group = data.payload?.groups.find((entry) => entry.id === groupId);
      return [...(group?.page.items ?? []), ...(data.extra[groupId] ?? [])];
    },
    [data.payload, data.extra],
  );

  const allRows = useCallback(
    (): readonly ItemRow[] => (data.payload?.groups ?? []).flatMap((group) => rowsOf(group.id)),
    [data.payload, rowsOf],
  );

  /**
   * The calendar and the gallery place *every* matching item, not a page, so they page to
   * exhaustion under a ceiling and say so when they hit it (`[V4§5]` decision D-V4-1).
   */
  const loadAll = useCallback(
    (ceiling: number) => {
      if (allRows().length >= ceiling) return;
      for (const [groupId, cursor] of Object.entries(data.cursors)) {
        if (cursor !== null) loadMore(groupId);
      }
    },
    [allRows, data.cursors, loadMore],
  );

  const context = useMemo<CellRenderContext>(
    () => ({
      people: data.payload?.people ?? [],
      columns: data.payload?.columns ?? [],
      search: '',
      timezone: 'UTC',
    }),
    [data.payload],
  );

  return {
    ...data,
    rowsOf,
    allRows,
    loadMore,
    loadAll,
    refresh: () => setNonce((value) => value + 1),
    context,
  };
}
